/**
 * "תמונת מצב" — המסך שהרכז יראה, מאחורי דגל התכונה `dashboard`.
 *
 * הדגל נשלט ממסך ניהול הדגלים: ההנהלה משחררת לקרן, לרכז, לסיווג או לכולם,
 * בלי פריסה. קדמה לו נעילה לשתי כתובות מייל בקוד; היא הוסרה.
 *
 * מה שנשאר לעשות: להעביר את החישוב ל-endpoint מרוכז בשרת (כרגע הכל מחושב
 * בלקוח מהנקודות הקיימות).
 *
 * המחרוזות בעברית ישירות ולא דרך i18n, ולכן גם משתמש באנגלית רואה את המסך
 * בעברית. העברתן ל-heb.json/en.json היא צעד נפרד.
 */

import { Children, cloneElement, isValidElement, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useFundContext from '@/contexts/Fund/useFundContext';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import { ChartEmpty, ChartSkeleton, ComboChart, Donut, Heatmap, RankList, Sparkline, type RankItem } from './charts';
import { CHART, currencySymbol, kMoney as kMoneyIn, money as moneyIn, nf } from './chartTheme';
import { DEFAULT_DASHBOARD_PERIOD, PERIOD_LABELS, effectiveFlowGranularity, useDashboardData, type CustomRange, type Granularity, type PeriodKey } from './useDashboardData';
import styles from './Dashboard.module.css';

/*
 * "תמיכות והוצאות" ירדה כלשונית - שלמה סימן אותה כמיותרת כולה. שני הכרטיסים
 * היחידים בה שהוא ביקש להציל: "אמצעי תשלום" עבר לסקירה הכללית, ו"הוצאות לפי
 * סוג" ירד לגמרי. השאר (בקשות לפי סטטוס, בקשות ממתינות לאישור, תמיכות ומלגות
 * ששולמו, יתרה מול התחייבויות, תמיכות לפי יעד ופירוט לפי יעד) ירדו איתה.
 */
type TabId = 'overview' | 'income';

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'סקירה כללית' },
  { id: 'income', label: 'תרומות והכנסות' },
];

const PERIODS: PeriodKey[] = ['week', 'month', 'quarter', 'year', 'custom'];

const iso = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** תאריך מהשרת ("2026-10-01T00:00:00") כ-1.10.2026, בלי לעבור דרך Date ואזור זמן. */
const dottedDate = (value: string): string => {
  const [y, m, d] = value.slice(0, 10).split('-');
  return `${Number(d)}.${Number(m)}.${y}`;
};

/**
 * ברירת המחדל של הבורר המותאם: מ-2020 ועד היום.
 *
 * משימה 1144: הבורר המותאם הוא הדרך להשוואת תרומות לפי שנים, ולכן הוא נפתח
 * ישר על טווח רב-שנתי במקום על חודש אחורה - כדי שהמעבר לתצוגה השנתית (בכרטיס
 * "תרומות לפי יום") יהיה מיידי בלי שהרכז יצטרך לדעת לשנות תאריך בעצמו.
 */
const defaultCustomRange = (): CustomRange => {
  const today = new Date();
  const from = new Date(2020, 0, 1);
  return { from: iso(from), to: iso(today) };
};

/**
 * ייצוא טבלה לקובץ שאקסל בעברית פותח נכון.
 * ה-BOM הוא מה שמונע ג׳יבריש; בלעדיו אקסל קורא UTF-8 כ-ANSI.
 */
const downloadCsv = (fileName: string, headers: string[], rows: (string | number)[][]) => {
  const cell = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map((row) => row.map(cell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

/** previous = null: אין תקופה להשוות אליה (טווח מותאם ארוך). אפס או פחות: אחוז שינוי חסר משמעות. */
const Trend = ({ current, previous, invert = false }: { current: number; previous: number | null; invert?: boolean }) => {
  if (previous === null || previous <= 0) return <span className={`${styles.trend} ${styles.flat}`}>—</span>;
  const diff = Math.round(((current - previous) / previous) * 100);
  const cls = diff === 0 ? styles.flat : (invert ? diff < 0 : diff > 0) ? styles.up : styles.down;
  return (
    <span className={`${styles.trend} ${cls}`}>
      {diff === 0 ? '' : diff > 0 ? '↑' : '↓'}
      {diff > 0 ? '+' : ''}
      {diff}%
    </span>
  );
};

const COLUMNS = 12;

/**
 * מחלק רוחבים כך שכל שורה מתמלאת.
 *
 * הרוחב שנכתב על כרטיס הוא מה שרוצים כשכל הכרטיסים נבנים, אבל כרטיס שלא נבנה -
 * וזה קורה לכל קרן שחסר לה מודול - השאיר שורה שמסתכמת בפחות מ-12 ולכן חור לבן
 * בצד. כאן השורות נבנות מהכרטיסים שבאמת קיימים, וכל שורה נמתחת יחסית לרוחב מלא:
 * כרטיס בודד שנשאר הופך למלבן על כל הרוחב, שניים מתחלקים ביניהם, וכן הלאה.
 */
const packSpans = (wanted: number[]): number[] => {
  const out = [...wanted];
  let index = 0;

  while (index < out.length) {
    const start = index;
    let sum = 0;
    while (index < out.length && sum + out[index] <= COLUMNS) {
      sum += out[index];
      index += 1;
    }
    // כרטיס רחב מ-12 בפני עצמו - לא אמור לקרות, אבל שלא ניתקע בלולאה.
    if (index === start) {
      out[start] = COLUMNS;
      index += 1;
      continue;
    }

    const row = out.slice(start, index);
    // מתיחה יחסית: כרטיס שהיה רחב יותר נשאר רחב יותר גם אחרי המתיחה.
    const stretched = row.map((value) => Math.max(1, Math.round((value * COLUMNS) / sum)));
    // עיגול יכול להחטיא ב-1 לכאן או לכאן; מתקנים על הכרטיס הגדול בשורה.
    let drift = COLUMNS - stretched.reduce((total, value) => total + value, 0);
    while (drift !== 0) {
      const target = stretched.indexOf(Math.max(...stretched));
      stretched[target] += drift > 0 ? 1 : -1;
      drift += drift > 0 ? -1 : 1;
    }
    stretched.forEach((value, offset) => {
      out[start + offset] = value;
    });
  }

  return out;
};

/**
 * הרשת של הלשונית. הכרטיסים נכתבים כרגיל בתוכה, וכאן נקבע הרוחב הסופי שלהם -
 * אחרי שידוע אילו מהם באמת נבנו. תנאי שלא התקיים מגיע לכאן כ-false ונופל
 * ב-Children.toArray, ולכן אין צורך לספור ידנית מי מוצג.
 */
const AutoGrid = ({ children }: { children: ReactNode }) => {
  const cards = Children.toArray(children).filter(isValidElement) as ReactElement<{ span: number }>[];
  const spans = packSpans(cards.map((card) => card.props.span ?? COLUMNS));
  return (
    <div className={styles.grid} role="tabpanel">
      {cards.map((card, index) => cloneElement(card, { span: spans[index] }))}
    </div>
  );
};

const Card = ({
  span,
  title,
  sub,
  tools,
  children,
}: {
  /** הרוחב המבוקש מתוך 12. AutoGrid עשוי למתוח אותו כדי להשלים שורה. */
  span: number;
  title: string;
  sub?: string;
  tools?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className={`${styles.card} ${span >= 7 ? styles.cardWide : ''}`} style={{ '--span': span } as React.CSSProperties}>
    <div className={styles.cardHead}>
      <div>
        <h2 className={styles.cardTitle}>{title}</h2>
        {sub && <p className={styles.cardSub}>{sub}</p>}
      </div>
      {tools && <div className={styles.cardTools}>{tools}</div>}
    </div>
    <div className={styles.cardBody}>{children}</div>
  </section>
);

const DashboardPreviewPage = () => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  // isSidebarDocked = מ-768 ומעלה. מתחתיו זה טלפון: שם 30 ימים כפול שתי
  // סדרות מקובצות נותנים עמודות של 4px, ולכן ברירת המחדל היא שבוע.
  const { isSidebarDocked } = useDeviceDetection();
  const [period, setPeriod] = useState<PeriodKey>(DEFAULT_DASHBOARD_PERIOD);
  const [flowGran, setFlowGran] = useState<Granularity>(() => (isSidebarDocked ? 'day' : 'week'));
  const [donMetric, setDonMetric] = useState<'sum' | 'count'>('sum');
  const [tab, setTab] = useState<TabId>('overview');
  const [customRange, setCustomRange] = useState<CustomRange>(defaultCustomRange);

  const { model, rangeLabel, compareLabel, isLoading, isError, isRequestsError, isAttentionLoading, isPendingDonationsEnabled, isPendingDonationsLoading, seesDonations, seesSupports, balanceRange } = useDashboardData(
    period,
    flowGran,
    period === 'custom' ? customRange : undefined
  );
  const k = model.kpi;

  /* מטבע הדיווח של הקרן — ILS או USD. כל סכום במסך עובר דרך העוטפים האלה,
     כדי שקרן דולרית לא תוצג עם ₪. */
  const cur = model.fundCurrency;
  const money = (n: number) => moneyIn(n, cur);
  const kMoney = (n: number) => kMoneyIn(n, cur);
  const curSign = currencySymbol(cur);

  /** אופן ההעברה בתרומה אופליין — באותה מילה שבה מסך התרומות קורא לו. */
  const donationTypeLabel = (type: string) => t(`donations.methods.${type === 'BankTransfer' ? 'ManualBankTransfer' : 'Check'}`);

  /* שם היעד נשמר בטבלה כמפתח אנגלי (למשל MonthlySupport), ולכן הוא עובר תרגום
     כמו אמצעי התשלום. מפתח שאינו בקבצי התרגום מוצג כמו שהוא. */
  const label = (key: string): string => {
    if (!key) return key;
    const translated = t(key);
    return translated === key ? key : translated;
  };

  const goalRank: RankItem[] = useMemo(
    () =>
      model.supportsByGoal.slice(0, 6).map((g) => ({
        name: label(g.name),
        value: g.amount,
        sub: `${g.families} משפחות`,
        color: CHART.wine,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model.supportsByGoal, t]
  );

  /* תוויות אמצעי התשלום מגיעות מקבצי התרגום, כמו במסך התנועות. */
  const paymentSlices = useMemo(
    () => model.channels.map((slice) => ({ name: label(slice.key), value: slice.value, color: slice.color })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model.channels, t]
  );

  /* כמה מדדים באמת נבנים ברצועה - שלושה קבועים, ועוד עד שניים: "ממתינות
     לאישור ראשוני" (מידע, לא התראה) ו"דורש טיפול" (רק סיווג שעומד לפוג).
     הרצועה מקבלת את המספר כמשתנה CSS ולא כרוחב מוכן: כך שוברי התצוגה
     לנייד ממשיכים לגבור עליו, מה שסגנון מוטבע היה מבטל. */
  const kpiCount =
    1 + (seesDonations ? 1 : 0) + (seesSupports ? 1 : 0) + (model.pendingInitialApproval ? 1 : 0) + (isAttentionLoading || model.attention.length > 0 ? 1 : 0);

  /*
   * האם יש בין הפריטים משהו שדורש פעולה ממש - בשונה מ"יש משהו להראות".
   * שלמה: הצגה דרמטית (רקע אדום, "עכשיו") מטעה כשכל מה שיש הוא בקשות
   * ממתינות לאישור משרדי - שם הרכז לא יכול לקדם דבר, רק לחכות. הרקע האדום
   * והתג נשמרים רק כשיש פריט critical אמיתי (השלמת מסמכים, סיווג שפג).
   */
  const hasCriticalAttention = model.attention.some((item) => item.severity === 'critical');

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <div className={styles.eyebrow}>תמונת מצב</div>
          <h1 className={styles.title}>{currentFund?.name ?? 'הפרויקט שלי'}</h1>
          <p className={styles.sub}>
            {rangeLabel} · כל הסכומים ב{cur === 'ILS' ? 'שקלים' : cur === 'USD' ? 'דולרים' : `מטבע ${cur}`}
            {model.unconvertedCount > 0 && ` · ${nf(model.unconvertedCount)} תנועות במטבע אחר לא נכללו, כי אין להן שער המרה`}
          </p>
        </div>
        <div className={styles.controls}>
          <div className={styles.seg} role="group" aria-label="בחירת תקופה">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={period === p}
                onClick={() => setPeriod(p)}
                title={p === 'custom' ? 'בחרו טווח תאריכים חופשי - כולל השוואת תרומות לפי שנים, כברירת מחדל מ-2020 ועד היום' : undefined}
              >
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div className={styles.dateRange}>
              <label>
                <span>מ־</span>
                <input
                  type="date"
                  value={customRange.from}
                  max={customRange.to}
                  onChange={(event) => setCustomRange((range) => ({ ...range, from: event.target.value }))}
                />
              </label>
              <label>
                <span>עד</span>
                <input
                  type="date"
                  value={customRange.to}
                  min={customRange.from}
                  max={iso(new Date())}
                  onChange={(event) => setCustomRange((range) => ({ ...range, to: event.target.value }))}
                />
              </label>
            </div>
          )}
          {/* כאן ישב כפתור "רענון" משלו. הוא הוסר: הוא קרא שוב לנדרים (מכסה של עשרים
              קריאות בשעה) ולא רענן את היתרה. הרענון של האזור האישי עושה את שניהם נכון. */}
          <span className={styles.stamp}>
            <span className={styles.dotLive} />
            {isLoading ? 'טוען נתונים…' : `${nf(model.counts.transactions)} תנועות בתקופה`}
          </span>
        </div>
      </div>

      {/* ============================ רצועת KPI ============================ */}
      <div className={styles.kpiRail} style={{ '--kpi-cols': kpiCount - 1 } as React.CSSProperties}>
        <div className={`${styles.kpi} ${styles.kpiHero}`}>
          <div className={styles.kpiTop}>
            <span className={styles.kpiLabel}>יתרה זמינה לתמיכות</span>
            <span className={`${styles.trend} ${styles.flat}`}>נכון לעכשיו</span>
          </div>
          <div className={styles.kpiValue}>
            <span className={styles.cur}>{curSign}</span>
            {k.availableBalance === null ? '—' : nf(k.availableBalance)}
          </div>
          {/* ארבעת המנוכים, ולא רק שניים: אחרת הפער בין סה״כ ליתרה זמינה נשאר בלי הסבר. */}
          <div className={styles.kpiSplit}>
            <div>
              <div className={styles.l}>סה״כ בפרויקט</div>
              <div className={styles.v}>{k.totalBalance === null ? '—' : money(k.totalBalance)}</div>
            </div>
            <div>
              <div className={styles.l}>ממתין לביצוע</div>
              <div className={styles.v}>{money(k.pendingExecution)}</div>
            </div>
            {k.frozenRequests > 0 && (
              <div>
                <div className={styles.l}>מוקפא</div>
                <div className={styles.v}>{money(k.frozenRequests)}</div>
              </div>
            )}
            {/* המסגרת נשמרת בסימן: חיובית היא סכום שנשמר בצד ויורד מהיתרה, ומסגרת
                אשראי (מינוס) שלילית ומוסיפה ליתרה. בלי השורה הזו הפער נשאר בלי הסבר. */}
            {k.overdraft > 0 && (
              <div>
                <div className={styles.l}>מסגרת</div>
                <div className={styles.v}>{money(k.overdraft)}</div>
              </div>
            )}
            {k.overdraft < 0 && (
              <div>
                {/* בלי סימן "+": ב-RTL הוא נדחק לקצה השני של המספר. המילה אומרת את זה. */}
                <div className={styles.l}>מסגרת אשראי (נוספת)</div>
                <div className={styles.v}>{money(-k.overdraft)}</div>
              </div>
            )}
            {k.devicesFreeze > 0 && (
              <div>
                <div className={styles.l}>מכשירי נדרים</div>
                <div className={styles.v}>{money(k.devicesFreeze)}</div>
              </div>
            )}
          </div>
        </div>

        {/* עולמות ההרשאה: מדד של עולם שסגור בפני הרכז לא מוצג. */}
        {seesDonations && (
        <div className={styles.kpi}>
          <div className={styles.kpiTop}>
            <span className={styles.kpiLabel}>תרומות בתקופה</span>
            {!isLoading && <Trend current={k.donations} previous={k.donationsPrev} />}
          </div>
          <div className={styles.kpiValue}>
            {isLoading ? (
              '…'
            ) : (
              <>
                <span className={styles.cur}>{curSign}</span>
                {nf(k.donations)}
              </>
            )}
          </div>
          <div className={styles.kpiFoot}>{isLoading ? 'טוען…' : <><b>{nf(k.donationsCount)}</b> תרומות{k.donationsPrev !== null && <> · {compareLabel}</>}</>}</div>
          {!isLoading && <Sparkline values={k.donationSpark} color={CHART.wine} />}
        </div>
        )}

        {seesSupports && (
        <div className={styles.kpi}>
          <div className={styles.kpiTop}>
            <span className={styles.kpiLabel}>תמיכות ומלגות שהועברו</span>
            {!isLoading && <Trend current={k.supports} previous={k.supportsPrev} />}
          </div>
          <div className={styles.kpiValue}>
            {isLoading ? (
              '…'
            ) : (
              <>
                <span className={styles.cur}>{curSign}</span>
                {nf(k.supports)}
              </>
            )}
          </div>
          <div className={styles.kpiFoot}>
            {isLoading ? (
              'טוען…'
            ) : (
              <>
                <b>{nf(k.supportFamilies)}</b> משפחות
                {k.supportFamilies > 0 && <> · ממוצע {money(k.supports / k.supportFamilies)}</>}
              </>
            )}
          </div>
          {!isLoading && <Sparkline values={k.supportSpark} color={CHART.steel} />}
        </div>
        )}

        {/* "הכנסה חוזרת" הוסר מהרצועה: הוראות קבע הן מודול שרוב הקרנות אינן
            משתמשות בו, ולכן המדד הראה מקף אצל רובן. מי שכן יש לו הוראות קבע
            רואה אותן בכרטיס המלא בסקירה, שגם הוא נבנה רק כשיש מה להראות. */}

        {/* בקשות ממתינות לאישור ראשוני — מידע, לא התראה: זה תקוע אצל המשרד
            ולא אצל הרכז, ואין לו איך "לקדם" את זה. לכן בלי צביעת אזעקה,
            ובכוונה מחוץ ל"דורש טיפול" - שלמה ביקש שלא יתערבבו. */}
        {model.pendingInitialApproval && (
          <div className={styles.kpi}>
            <div className={styles.kpiTop}>
              <span className={styles.kpiLabel}>ממתינות לאישור ראשוני מעל שבוע</span>
              <span className={`${styles.trend} ${styles.flat}`}>במשרד</span>
            </div>
            <div className={styles.kpiValue}>{nf(model.pendingInitialApproval.count)}</div>
            <div className={styles.kpiFoot}>
              <Link to={model.pendingInitialApproval.to}>
                {model.pendingInitialApproval.oldestAt !== null
                  ? `הוותיקה מ-${new Date(model.pendingInitialApproval.oldestAt).toLocaleDateString('he-IL')}`
                  : 'לצפייה ברשימה'}
              </Link>
            </div>
          </div>
        )}

        {/* דורש טיפול — נתמכים שהסיווג שלהם עומד לפוג בלבד. שלמה: זה משהו
            שהרכז באמת יכול לפעול עליו (לחדש סיווג), בשונה מבקשות שממתינות
            לאישור המשרד. מוצג רק כשיש מה לטפל בו. */}
        {(isAttentionLoading || model.attention.length > 0) && (
          <div className={`${styles.kpi} ${hasCriticalAttention ? styles.kpiAlert : ''}`}>
            <div className={styles.kpiTop}>
              <span className={styles.kpiLabel}>דורש טיפול</span>
              {hasCriticalAttention && <span className={`${styles.pill} ${styles.pillCrit}`}>עכשיו</span>}
            </div>
            <p className={styles.kpiCaption}>נתמכים שהסיווג שלהם עומד לפוג</p>
            <div className={styles.kpiValue}>{isAttentionLoading ? '—' : nf(k.attentionCount)}</div>
            <div className={styles.kpiFoot}>
              {isAttentionLoading
                ? 'בודק בקשות ונתמכים…'
                : model.attention
                    .slice(0, 2)
                    .map((item) => item.title)
                    .join(' · ')}
            </div>
          </div>
        )}
      </div>

      {/* ============================ לשוניות ============================ */}
      <div className={styles.tabs} role="tablist" aria-label="חתכי הדשבורד">
        {TABS.filter((tb) => tb.id !== 'income' || seesDonations).map((tb) => (
          <button key={tb.id} type="button" role="tab" aria-selected={tab === tb.id} className={styles.tab} onClick={() => setTab(tb.id)}>
            {tb.label}
          </button>
        ))}
      </div>

      {isError && <ChartEmpty height={220} text="לא הצלחנו לטעון את הנתונים. נסו לרענן את העמוד." />}

      {!isError && tab === 'overview' && (
        <AutoGrid>
          <Card
            span={8}
            title="תזרים הפרויקט"
            sub={model.flowLineIsFundBalance ? 'נכנס מול יוצא, וקו יתרת הפרויקט מעליהם' : 'נכנס מול יוצא, ומעליהם השינוי המצטבר בתקופה'}
            tools={
              <div className={styles.mini}>
                {/* בטווח ארוך הגרף עולה לבד לרזולוציה גסה יותר (effectiveFlowGranularity),
                    ולכן הכפתור המסומן הוא מה שמוצג בפועל, והעדינים ממנו כבויים. */}
                {(['day', 'week', 'month'] as Granularity[]).map((g) => {
                  const available = effectiveFlowGranularity(g, model.periodDays) === g;
                  return (
                    <button
                      key={g}
                      type="button"
                      aria-pressed={model.flowGranularity === g}
                      disabled={!available}
                      title={available ? undefined : 'הטווח ארוך מדי לתצוגה הזו'}
                      onClick={() => setFlowGran(g)}
                    >
                      {g === 'day' ? 'יום' : g === 'week' ? 'שבוע' : 'חודש'}
                    </button>
                  );
                })}
              </div>
            }
          >
            {/* משימה 1448: עם כמה התחלתי, עם כמה סיימתי ומה השתנה - כמו בתדפיס בנק.
                השינוי כולל עמלות, שאינן בטבלת התנועות, ולכן הוא לא תמיד נכנס-פחות-יוצא שבגרף. */}
            {balanceRange && (
              <div className={styles.balanceRange}>
                <div>
                  <div className={styles.l}>יתרה בפתיחה · {dottedDate(balanceRange.start)}</div>
                  <div className={styles.v}>{money(balanceRange.openingBalance)}</div>
                </div>
                <div>
                  <div className={styles.l}>יתרה בסגירה · {dottedDate(balanceRange.end)}</div>
                  <div className={styles.v}>{money(balanceRange.closingBalance)}</div>
                </div>
                <div>
                  {/* בלי סימן +/-: ב-RTL הוא נדחק לקצה השני של המספר. המילה אומרת את הכיוון. */}
                  <div className={styles.l}>
                    {balanceRange.closingBalance >= balanceRange.openingBalance ? 'גידול בתקופה' : 'ירידה בתקופה'}
                  </div>
                  <div className={`${styles.v} ${balanceRange.closingBalance >= balanceRange.openingBalance ? styles.balanceUp : styles.balanceDown}`}>
                    {money(Math.abs(balanceRange.closingBalance - balanceRange.openingBalance))}
                  </div>
                </div>
                <p className={styles.balanceRangeNote}>כולל עמלות ודמי מסופון, שאינם מופיעים בטבלת התנועות. הפירוט המלא ב״ייצוא טבלה״.</p>
              </div>
            )}
            {isLoading ? (
              <ChartSkeleton height={262} />
            ) : model.flow.length ? (
              <>
                <ComboChart
                  ariaLabel="תזרים הפרויקט — נכנס מול יוצא"
                  data={model.flow}
                  height={262}
                  bars={[
                    { key: 'income', name: 'נכנס', color: CHART.wine },
                    { key: 'expense', name: 'יוצא', color: CHART.steelSoft },
                  ]}
                  line={{ key: 'balance', name: model.flowLineIsFundBalance ? 'יתרת הפרויקט' : 'שינוי מצטבר בתקופה', color: CHART.teal, fmt: kMoney }}
                  fmtAxis={kMoney}
                  fmtTip={money}
                />
                <div className={styles.legend}>
                  <span>
                    <i style={{ background: CHART.wine }} />
                    נכנס — תרומות, פיקדונות והלוואות
                  </span>
                  <span>
                    <i style={{ background: CHART.steelSoft }} />
                    יוצא — תמיכות, מלגות, ספקים ועמלות
                  </span>
                  <span>
                    <i style={{ background: CHART.teal, borderRadius: '50%' }} />
                    {model.flowLineIsFundBalance ? 'יתרת הפרויקט (ציר שמאלי)' : 'שינוי מצטבר בתקופה (ציר שמאלי) — לתנועות האלה אין יתרה מהשרת'}
                  </span>
                </div>
              </>
            ) : (
              <ChartEmpty height={262} />
            )}
          </Card>

          {/* היה "הרכב היתרה" — נכנס/יוצא לפי סוג תנועה. שלמה: הכותרת לא נכונה,
              אמור להראות תרומות לפי אמצעי תשלום בלבד, לא מה שהוצג. הוחלף
              במפורש לפי זה. הכרטיס הכפול "אמצעי תשלום" שישב בהמשך העמוד הוסר. */}
          {seesDonations && (
            <Card span={4} title="תרומות לפי אמצעי תשלום" sub="איך התרומות הגיעו, לפי מה שנרשם בתנועה עצמה">
              {isLoading ? <ChartSkeleton height={160} /> : <Donut items={paymentSlices} centerLabel="סך תרומות" fmt={kMoney} />}
            </Card>
          )}

          {/* דורש טיפול — הכרטיס שממנו הרכז יוצא לפעולה, ולכן כל שורה היא קישור.
              כמו המדד ברצועה, הוא נבנה רק כשיש מה לטפל בו. */}
          {(isAttentionLoading || model.attention.length > 0) && (
            <Card span={4} title="דורש טיפול" sub="נתמכים שהסיווג שלהם עומד לפוג">
              {isAttentionLoading ? (
                <ChartSkeleton height={160} />
              ) : (
                <div className={styles.todo}>
                  {model.attention.map((item) => (
                    <Link key={item.id} to={item.to} className={styles.todoRow}>
                      <span className={`${styles.todoDot} ${item.severity === 'critical' ? styles.todoDotCrit : styles.todoDotWarn}`} aria-hidden />
                      <span className={styles.todoText}>
                        <span className={styles.todoName}>{item.title}</span>
                        {item.detail && <span className={styles.todoSub}>{item.detail}</span>}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* הוראות קבע — רק לקרן שיש לה. אצל השאר הכרטיס היה טקסט שאומר "אין". */}
          {model.standingOrders && (
            <Card span={4} title="הכנסה חוזרת" sub="הוראות קבע — הבסיס הקבוע של הפרויקט">
              <>
                <div className={styles.microRow}>
                  <div className={styles.micro}>
                    <div className={styles.l}>גבייה צפויה</div>
                    {/* כל מטבע בשורה משלו - שקלים ודולרים אינם מתחברים למספר אחד. */}
                    {model.standingOrders.expected.length ? (
                      model.standingOrders.expected.map((row) => (
                        <div key={row.currency} className={styles.v} style={{ color: CHART.wine }}>
                          {moneyIn(row.amount, row.currency)}
                        </div>
                      ))
                    ) : (
                      <div className={styles.v} style={{ color: CHART.wine }}>
                        {money(0)}
                      </div>
                    )}
                  </div>
                  <div className={styles.micro}>
                    <div className={styles.l}>פעילות</div>
                    <div className={styles.v}>{nf(model.standingOrders.active)}</div>
                  </div>
                  <div className={styles.micro}>
                    <div className={styles.l}>סה״כ הוראות</div>
                    <div className={styles.v}>{nf(model.standingOrders.total)}</div>
                  </div>
                </div>
                <p className={styles.cardSub} style={{ marginTop: 14 }}>
                  התפלגות ההוראות הפעילות לפי סכום החיוב
                  {model.standingOrders.otherCurrencyActive > 0 &&
                    `, ב${cur === 'ILS' ? 'שקלים' : 'דולרים'} בלבד. ${nf(model.standingOrders.otherCurrencyActive)} הוראות במטבע אחר לא נכללו.`}
                </p>
                <ComboChart
                  ariaLabel="הוראות קבע לפי סכום חיוב"
                  data={model.standingOrders.byAmount}
                  height={186}
                  bars={[{ key: 'v', name: 'הוראות קבע', color: CHART.wine }]}
                  fmtAxis={(v) => nf(v)}
                  fmtTip={(v) => `${nf(v)} הוראות`}
                />
              </>
            </Card>
          )}

          {seesSupports && (
            <Card span={4} title="יעדי תמיכה מובילים" sub="לפי הסכום המאושר לחודשים שבתקופה">
              {isLoading ? (
                <ChartSkeleton height={160} />
              ) : isRequestsError ? (
                <ChartEmpty height={140} text="לא הצלחנו לטעון את בקשות התמיכה" />
              ) : (
                <RankList items={goalRank} fmt={kMoney} />
              )}
            </Card>
          )}

          {/* עשרת התורמים המובילים — הועבר לכאן מלשונית התרומות, כי זו השאלה
              הראשונה שרכז שואל כשהוא נכנס למסך. הוא לא מוכפל: בלשונית התרומות
              הוא כבר לא מופיע. */}
          {seesDonations && (
            <Card span={6} title="עשרת התורמים המובילים" sub="בתקופה הנבחרת">
              {isLoading ? <ChartSkeleton height={200} /> : <RankList items={model.topDonors} fmt={kMoney} />}
            </Card>
          )}

          {/* עשר התמיכות האחרונות — מהתנועות בפועל, כלומר כסף שכבר יצא.
              בלי tblScroll: הרשימה תמיד עד 10 שורות (recentSupports חתוכה ל-10
              ב-useDashboardData), ו-264px של tblScroll חתכו אותה באמצע השורה
              השביעית ונראה כאילו הכרטיס "לא ממלא עד הסוף". */}
          {seesSupports && (
          <Card
            span={6}
            title="עשר התמיכות האחרונות"
            sub="תמיכות ומלגות ששולמו בפועל, מהחדשה לישנה"
            tools={
              model.recentSupports.length ? (
                <button
                  type="button"
                  className={styles.ghost}
                  onClick={() =>
                    downloadCsv(
                      `תמיכות-אחרונות-${rangeLabel}.csv`,
                      ['תאריך', 'נתמך', 'סוג', `סכום (${cur})`],
                      model.recentSupports.map((row) => [row.date.toLocaleDateString('he-IL'), row.name, row.kind, Math.round(row.amount)])
                    )
                  }
                >
                  ייצוא
                </button>
              ) : undefined
            }
          >
            <div className={styles.scrollX}>
              {isLoading ? (
                <ChartSkeleton height={200} />
              ) : model.recentSupports.length ? (
                <table className={styles.tbl}>
                  <thead>
                    <tr>
                      <th>תאריך</th>
                      <th>נתמך</th>
                      <th className={styles.colSecondary}>סוג</th>
                      <th>סכום</th>
                    </tr>
                  </thead>
                  <tbody>
                    {model.recentSupports.map((row) => (
                      <tr key={row.key}>
                        <td className={styles.n} style={{ color: CHART.ink3 }}>
                          {row.date.toLocaleDateString('he-IL')}
                        </td>
                        <td style={{ fontWeight: 500 }}>{row.name}</td>
                        <td className={styles.colSecondary} style={{ color: CHART.ink2 }}>
                          {row.kind}
                        </td>
                        <td className={styles.n}>{money(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <ChartEmpty height={160} text="לא הוגשו בקשות תמיכה לתקופה זו" />
              )}
            </div>
          </Card>
          )}

          {/* כאן ישב "הקרנות שלי". הוא הוסר: הרכז נמצא בעמוד של קרן מסוימת,
              והמעבר בין קרנות כבר יושב בבורר הקרנות שבהדר. במקומו — עשרת
              התורמים המובילים, שלמעלה. */}
        </AutoGrid>
      )}

      {!isError && tab === 'income' && seesDonations && (
        <AutoGrid>
          <Card
            span={8}
            title={
              model.donationBucketGran === 'year' ? 'תרומות לפי שנה' : model.donationBucketGran === 'month' ? 'תרומות לפי חודש' : 'תרומות לפי יום'
            }
            sub={
              model.donationBucketGran === 'year'
                ? 'כל עמודה = שנה. בתצוגה שנתית אין ממוצע נע'
                : model.donationBucketGran === 'month'
                  ? 'כל עמודה = חודש. הקו הבהיר הוא ממוצע נע של 7 חודשים'
                  : 'כל עמודה = יום. הקו הבהיר הוא ממוצע נע של 7 ימים'
            }
            tools={
              <div className={styles.mini}>
                <button type="button" aria-pressed={donMetric === 'sum'} onClick={() => setDonMetric('sum')}>
                  סכום
                </button>
                <button type="button" aria-pressed={donMetric === 'count'} onClick={() => setDonMetric('count')}>
                  מספר תרומות
                </button>
              </div>
            }
          >
            {isLoading ? (
              <ChartSkeleton height={250} />
            ) : (
              <ComboChart
                ariaLabel={model.donationBucketGran === 'year' ? 'תרומות לפי שנה' : model.donationBucketGran === 'month' ? 'תרומות לפי חודש' : 'תרומות לפי יום'}
                data={donMetric === 'sum' ? model.donationsBySum : model.donationsByCount}
                height={250}
                bars={[{ key: 'v', name: donMetric === 'sum' ? 'תרומות' : 'מספר תרומות', color: CHART.wine }]}
                line={
                  model.donationBucketGran === 'year'
                    ? undefined
                    : {
                        key: 'ma',
                        name: model.donationBucketGran === 'month' ? 'ממוצע נע 7 חודשים' : 'ממוצע נע 7 ימים',
                        color: CHART.brass,
                        fmt: donMetric === 'sum' ? kMoney : (v) => nf(v),
                      }
                }
                fmtAxis={donMetric === 'sum' ? kMoney : (v) => nf(v)}
                fmtTip={donMetric === 'sum' ? money : (v) => `${nf(v)} תרומות`}
              />
            )}
          </Card>

          {/* עוגת אמצעי התשלום עברה ללשונית "תמיכות והוצאות", למקום שבו ישבה
              עוגת ההוצאות. היא לא מוכפלת כאן. */}

          <Card span={6} title="מתי תורמים" sub="בכרטיס אשראי זה יום התרומה; בהעברה בנקאית זה יום הזיכוי בבנק">
            {isLoading ? (
              <ChartSkeleton height={180} />
            ) : (
              // בטווח ארוך (חודש-מותאם רב-שנתי) יש עשרות ואף מאות שבועות. הרשת
              // עצמה נשארת בגובה קבוע (ראו .hmScroll ב-Heatmap) וגוללת פנימית
              // במקום להימתח - העמוד כולו לא מתארך.
              <Heatmap rows={model.heat.rows} cols={model.heat.cols} values={model.heat.values} color={CHART.wine} metric="תרומות" fmt={(v) => `${nf(v)} תרומות`} />
            )}
          </Card>

          <Card span={6} title="גובה התרומה" sub="תרומה שנפרסה לתשלומים נמדדת לפי כל חיוב בנפרד">
            {isLoading ? (
              <ChartSkeleton height={236} />
            ) : (
              <>
                <ComboChart
                  ariaLabel="התפלגות גובה החיוב"
                  data={model.amountHist}
                  height={236}
                  bars={[{ key: 'v', name: 'חיובים', color: CHART.steelMid }]}
                  fmtAxis={(v) => nf(v)}
                  fmtTip={(v) => `${nf(v)} חיובים`}
                />
                <div className={styles.microRow} style={{ marginTop: 14 }}>
                  <div className={styles.micro}>
                    <div className={styles.l}>חיוב ממוצע</div>
                    <div className={styles.v}>{money(model.amountStats.avg)}</div>
                  </div>
                  <div className={styles.micro}>
                    <div className={styles.l}>חיוב חציוני</div>
                    <div className={styles.v}>{money(model.amountStats.median)}</div>
                  </div>
                  <div className={styles.micro}>
                    <div className={styles.l}>החיוב הגדול</div>
                    <div className={styles.v}>{money(model.amountStats.max)}</div>
                  </div>
                </div>
              </>
            )}
          </Card>

          {/* "התורמים המובילים" עבר לסקירה הכללית. */}

          <Card
            span={6}
            title="תורמים חדשים מול חוזרים"
            sub={
              model.kpi.donationsPrev === null
                ? 'מספר התרומות. ״חדש״ = התרומה הראשונה של התורם בטווח שנבחר'
                : 'מספר התרומות. ״חדש״ = תורם שלא תרם קודם, בתקופה ובתקופה שלפניה באותו אורך'
            }
          >
            {isLoading ? (
              <ChartSkeleton height={200} />
            ) : (
              <>
                <ComboChart
                  ariaLabel="תורמים חדשים מול חוזרים"
                  data={model.newVsReturning}
                  height={200}
                  stacked
                  bars={[
                    { key: 'ret', name: 'תורם חוזר', color: CHART.wine },
                    { key: 'nw', name: 'תורם חדש', color: CHART.tealSoft },
                  ]}
                  fmtAxis={(v) => nf(v)}
                  fmtTip={(v) => `${nf(v)} תרומות`}
                />
                <div className={styles.legend}>
                  <span>
                    <i style={{ background: CHART.wine }} />
                    תורם חוזר
                  </span>
                  <span>
                    <i style={{ background: CHART.tealSoft }} />
                    תורם חדש
                  </span>
                </div>
              </>
            )}
          </Card>

          <Card
            span={6}
            title="תרומות אחרונות"
            sub="שש התנועות האחרונות שנקלטו"
            tools={
              model.recent.length ? (
                <button
                  type="button"
                  className={styles.ghost}
                  onClick={() =>
                    downloadCsv(
                      `תרומות-אחרונות-${rangeLabel}.csv`,
                      ['תאריך', 'תורם', 'אמצעי', `סכום (${cur})`],
                      model.recent.map((row) => [row.date.toLocaleDateString('he-IL'), row.donor, label(row.paymentMethod), Math.round(row.amount)])
                    )
                  }
                >
                  ייצוא
                </button>
              ) : undefined
            }
          >
            <div className={styles.scrollX}>
              {isLoading ? (
                <ChartSkeleton height={160} />
              ) : model.recent.length ? (
                <table className={styles.tbl}>
                  <thead>
                    <tr>
                      <th>תאריך</th>
                      <th>תורם</th>
                      <th className={styles.colSecondary}>אמצעי</th>
                      <th>סכום</th>
                    </tr>
                  </thead>
                  <tbody>
                    {model.recent.map((row) => (
                      <tr key={row.key}>
                        <td className={styles.n} style={{ color: CHART.ink3 }}>
                          {row.date.toLocaleDateString('he-IL')}
                        </td>
                        <td style={{ fontWeight: 500 }}>{row.donor}</td>
                        <td className={styles.colSecondary} style={{ color: CHART.ink2 }}>
                          {label(row.paymentMethod)}
                        </td>
                        <td className={styles.n}>{money(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <ChartEmpty height={160} />
              )}
            </div>
          </Card>

          {/* תרומות שממתינות לאישור — רק לקרן שמודול התרומות פתוח אצלה. */}
          {isPendingDonationsEnabled && (
            <Card span={6} title="תרומות ממתינות לאישור" sub="תרומות בהעברה בנקאית או בצ׳ק שנקלטו וטרם אושרו">
              <div className={styles.scrollX}>
                {isPendingDonationsLoading ? (
                  <ChartSkeleton height={160} />
                ) : model.pendingDonations.length ? (
                  <table className={styles.tbl}>
                    <thead>
                      <tr>
                        <th>תורם</th>
                        <th className={styles.colSecondary}>אופן ההעברה</th>
                        <th>סכום</th>
                        <th>ממתינה</th>
                      </tr>
                    </thead>
                    <tbody>
                      {model.pendingDonations.map((donation) => (
                        <tr key={donation.id}>
                          <td style={{ fontWeight: 500 }}>{donation.donor}</td>
                          <td className={styles.colSecondary} style={{ color: CHART.ink2 }}>
                            {donationTypeLabel(donation.type)}
                          </td>
                          <td className={styles.n}>{moneyIn(donation.amount, donation.currency)}</td>
                          <td className={styles.n} style={{ color: (donation.ageDays ?? 0) >= 3 ? CHART.crit : CHART.ink3 }}>
                            {donation.ageDays === null ? '—' : donation.ageDays === 0 ? 'היום' : `${nf(donation.ageDays)} ימים`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <ChartEmpty height={160} text="אין תרומות שממתינות לאישור" />
                )}
              </div>
            </Card>
          )}
        </AutoGrid>
      )}
    </div>
  );
};

export default DashboardPreviewPage;
